import { ReportPreviewData, ReportGenerationOptions, ReportType } from "../types";
import { HoldingWithInstrumentAndAccount, TransactionWithInstrumentAndAccount } from "@/types";

interface GenerateCSVOptions {
  reportData: ReportPreviewData;
  reportOptions: ReportGenerationOptions;
  reportType: ReportType;
  holdings: HoldingWithInstrumentAndAccount[];
  transactions: TransactionWithInstrumentAndAccount[];
}

/**
 * Generates a CSV blob representing the requested report sections. The CSV is
 * structured as a simple, human-readable document that can be opened in
 * spreadsheet applications such as Excel.
 */
export const generateCSVReport = ({
  reportData,
  reportOptions,
  reportType,
  holdings,
  transactions,
}: GenerateCSVOptions): Blob => {
  const lines: string[] = [];

  // Report header / meta-data
  lines.push("Report Type," + reportType);
  lines.push("Fund," + escapeCsv(reportData.fundName));
  lines.push("Fund Key," + escapeCsv(reportData.fundKey));
  lines.push("Reporting Period," + escapeCsv(reportData.reportingPeriod));
  lines.push("Generated," + escapeCsv(reportData.asOfDate));
  lines.push(""); // empty line

  // NAV Summary
  if (reportOptions.includeNAVSummary && reportData.navSummary) {
    lines.push("NAV Summary");
    lines.push("Currency,Total NAV,Total Holdings,Confirmed Holdings,Unconfirmed Holdings,As Of Date");
    lines.push([
      reportData.navSummary.currency,
      reportData.navSummary.totalNAV,
      reportData.navSummary.totalHoldings,
      reportData.navSummary.confirmedHoldings,
      reportData.navSummary.unconfirmedHoldings,
      reportData.navSummary.asOfDate,
    ].join(","));
    lines.push("");
  }

  // Holdings Detail
  if (reportOptions.includeHoldings) {
    lines.push(`Holdings Detail (${holdings.length} positions)`);
    lines.push("Symbol,Instrument Name,Quantity,Base Amount,Currency,Status,Account");
    holdings.forEach((h) => {
      lines.push([
        escapeCsv(h.instrument.instrumentKey),
        escapeCsv(h.instrument.name),
        h.quantity,
        h.baseAmount,
        h.currency ?? "",
        h.isConfirmed ? "Confirmed" : "Unconfirmed",
        escapeCsv(h.account.accountKey),
      ].join(","));
    });
    lines.push("");
  }

  // Transaction Summary – we always include the full transaction list because
  // CSVs are lightweight and users typically expect raw data.
  lines.push(`Transactions (${transactions.length})`);
  lines.push("Date,Type,Symbol,Quantity,Amount,Currency,Status");
  transactions.forEach((t) => {
    lines.push([
      formatDate(t.transactionDate),
      t.transactionType,
      escapeCsv(t.instrument.instrumentKey),
      t.quantity,
      t.baseAmount,
      t.currency ?? "",
      t.isConfirmed ? "Confirmed" : "Unconfirmed",
    ].join(","));
  });

  const csvContent = lines.join("\r\n");
  return new Blob([csvContent], { type: "text/csv;charset=utf-8" });
};

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Escape CSV field values that contain commas, quotes or line breaks per RFC 4180.
function escapeCsv(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return "";
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
} 